/**
 *  App component
 *
 *  @author    2024        김효민
 *  @author    2026-04-23  Bruce.Cheong    (디자인 개선)
 */

/** External import */
import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { create } from "zustand";


/** Internal import */
import CSS from "./App.module.css";

import { appVersionManagerClass, backPointUrlManagerClass, deleteAllCookies, setCookie, webViewBridgeClass } from './Common/ts/appTools';
import { callAxios, dev_console, getAccountData, isMaintenancePageOn, isSuccess, openInExternalBrowser, whatIsMode, whichBrowserDoYouUse } from './Common/ts/commonTools';

// 공용 페이지
import Login from './PublicPage/Login/Login';
import Header from './PublicPage/Header/Header';


import ResetUserPassword from './PublicPage/ResetUserPassword/ResetUserPassword';

import ThemeBookHome from './PublicPage/ThemeBook/ThemeBookHome';

import ThemeBookManagement from './AdminPage/ThemeBookManagement/ThemeBookManagement';

import { defaultApiReaponseInterface, tokenCheckInterface } from './Common/interface/Common.interface';
import DeleteURL from './PublicPage/DeleteURL/DeleteURL';
import TestPage from './TestPage/TestPage';
import Toast from './Component/Elements/Toast/Toast';
import CustomerNavi from './PublicPage/Navi/CustomerNavi';
import AdminNavi from './PublicPage/Navi/AdminNavi';
import RedirectToAnotherBrowser from './PublicPage/RedirectToAnotherBrowser/RedirectToAnotherBrowser';
import { mobileBackgroundColorType } from './Common/interface/style.interface';
import { BROWSER_CODE } from './Common/ts/const';
import MaintenancePage from './PublicPage/MaintenancePage/MaintenancePage';
import { RTN_interface } from './Common/interface/App.interface';
import {
    canAccessOopsTheme,
    resolveEffectiveOopsUserType,
} from './Common/ts/oopsThemeAccess';
import useAlarmNewCountREST from './PublicPage/Header/hooks/useAlarmNewCountREST.hook';
import useAlarmListREST from './PublicPage/Header/hooks/useAlarmListREST.hook';
import { ErrorBoundaryRapper } from './Component/Elements/ErrorBoundaries/ErrorBoundaryRapper';
import ErrorBoundary from './Component/Elements/ErrorBoundaries/ErrorBoundary';

import ReservationHome from './CustomerPage/Reservation/ReservationHome';   // 고객 페이지
import SelectTheme from './CustomerPage/Reservation/SelectTheme';
import PhotoListMain from './CustomerPage/PhotoList/PhotoListMain';
import PhotoDetail from './CustomerPage/PhotoList/PhotoDetail';
import PaymentHistory from './CustomerPage/PaymentHistory/PaymentHistory';
import ProductList from './CustomerPage/ProductManufacturing/ProductList';
import ProductDetail from './CustomerPage/ProductManufacturing/ProductDetail';
import ProductRequestEdit from './CustomerPage/ProductManufacturing/ProductRequestEdit';
import ProductConfirm from './CustomerPage/ProductManufacturing/ProductConfirm';
import MainHome from './CustomerPage/MainHome/MainHome';
import ChangePassword from './CustomerPage/ChangePassword/ChangePassword';
import MyInfo from './CustomerPage/MyInfo/MyInfo';  // MyPage는 새로 만들고 MyInfo로 대체(Bruce.Cheong 2026-05-06)

/** My Home(New) Bruce.Choeng (2026-04-27) */
import MyHome from './CustomerPage/MyHome/MyHome';
import MyPage from './CustomerPage/MyPage/MyPage';
import QnaList from './CustomerPage/Qna/QnaList';
import FaqList from './CustomerPage/Faq/FaqList';


const webViewBridge = new webViewBridgeClass();

const appVersionManager = new appVersionManagerClass();

const backPointUrlManager = new backPointUrlManagerClass();

type commonState_Type = {
    isHeaderHidden: boolean;
    headerLeftNode: React.ReactNode | null;
    mobileHeaderTitle: string;
    headerRightNode: React.ReactNode | null;
    headerBackgroundColorPreset: mobileBackgroundColorType;
    isNaviHidden: boolean;
}

type commonStateSetter_Type = {
    setIsHeaderHidden: (value: boolean) => void;
    setHeaderLeftNode: (value: React.ReactNode | null) => void;
    setMobileHeaderTitle: (value: string) => void;
    setHeaderRightNode: (value: React.ReactNode | null) => void;
    setHeaderBackgroundColorPreset: (value: mobileBackgroundColorType) => void;
    setIsNaviHidden: (value: boolean) => void;
}

export const useCommonStore = create<commonState_Type & commonStateSetter_Type>((set) => ({
    isHeaderHidden: false,
    headerLeftNode: null,
    mobileHeaderTitle: "",
    headerRightNode: null,
    headerBackgroundColorPreset: "",
    isNaviHidden: false,

    setIsHeaderHidden: (value: boolean) => set({isHeaderHidden: value}),
    setHeaderLeftNode: (value: React.ReactNode | null) => set({headerLeftNode: value}),
    setMobileHeaderTitle: (value: string) => set({mobileHeaderTitle: value}),
    setHeaderRightNode: (value: React.ReactNode | null) => set({headerRightNode: value}),
    setHeaderBackgroundColorPreset: (value: mobileBackgroundColorType) => set({headerBackgroundColorPreset: value}),
    setIsNaviHidden: (value: boolean) => set({isNaviHidden: value}),
}))

function App() {

    // 접속한 환경이 카카오톡, 라인 인앱브라우저인 경우 외부 브라우저에서 열리게 한다.
    openInExternalBrowser.auto_open();

    const pageRouterCode_obj = {
        none: 0,
        login: 1,
        customer: 2,
        admin: 3,
    } as const;

    type pageRouterCode_type = typeof pageRouterCode_obj [keyof typeof pageRouterCode_obj];

    const [pageRouterCode, setPageRouterCode] = useState<pageRouterCode_type>(pageRouterCode_obj.none);

    const isHeaderHidden = useCommonStore((state) => state.isHeaderHidden);
    const headerLeftNode = useCommonStore((state) => state.headerLeftNode);
    const mobileHeaderTitle = useCommonStore((state) => state.mobileHeaderTitle);
    const headerRightNode = useCommonStore((state) => state.headerRightNode);
    const headerBackgroundColorPreset = useCommonStore((state) => state.headerBackgroundColorPreset);
    const isNaviHidden = useCommonStore((state) => state.isNaviHidden);

    const [URLSearchParamsValue, SetURLSearchParamsValue] = useSearchParams();

    // 실제로 컨텐츠들이 출력되는 영역의 ref
    const containerRef = useRef<HTMLDivElement>(null);

    // 서버 개발중 페에지 출력 여부
    const l_isMaintenancePageOn = isMaintenancePageOn();

    async function sendDeviceInfo(){
        // flutter에 모바일기기 고유 토큰값을 요청
        const m_deviceToken_data = await webViewBridge.getAuthToken();

        SendDeviceData:{
            // flutter에서 정상적으로 토큰을 확보한 경우에만 서버로 요청을 보낸다.(즉 웹환경 등일때는 동작 안함)
            if(!m_deviceToken_data.sign || !m_deviceToken_data.data){
                break SendDeviceData;
            }

            try{
                const m_device_save_url = "api/v3/oops/customer/device/save";

                const m_config = {
                    device_token: m_deviceToken_data.data.fcmToken,
                    device_type: m_deviceToken_data.data.deviceType,
                    app_version: m_deviceToken_data.data.appVersion,
                }

                const m_response = await callAxios.api({
                    url: m_device_save_url,
                    method: "post",
                    data: m_config,
                })

                dev_console.log(m_device_save_url);
                dev_console.log(m_response);
            }catch(error){
                dev_console.error(error, "Error sendDeviceInfo");
            }

            // 2026-02-12 앱 심사를 위해 앱 버전 체크 기능 중단
            // 버전 체크
            // if(process.env.REACT_APP_MODE === "PRODUCT"){
            //     if(m_deviceToken_data.data.appVersion){
            //         // flutter에 app의 최신 버전 정보를 넘긴다.
            //         await webViewBridge.checkAppVersion(appVersionManager.latestAppVersion);
            //     }else{
            //         // App Version관리 패치가 적용되지 않는 app사용자가 있을 경우를 대비하여 해당경우에만 alert생성
            //         alert("OOPS의 버전이 낮습니다.\n\nOOPS를 최신 버전으로 업데이트해 주세요.");
            //     }
            // }
        }
    }

    // 토큰을 서버로 보내서 검증하는 함수.
    async function authTokenChecker(token: string){
        const RTN: RTN_interface<number> = {
            sign: false,
            data: 0,
            msg: ""
        }

        IsHaveToken:{
            if(!token){
                RTN.sign = true;
                RTN.data = -1;
                RTN.msg = "have not token";
                break IsHaveToken;
            }
        }

        let l_data: defaultApiReaponseInterface<tokenCheckInterface> | null = null;
        AccountDataReqeuset:{
            if(RTN.sign){
                break AccountDataReqeuset;
            }

            // 토큰값 검증
            const web_token_api = 'api/v3/accounts/web-token';

            try{
                const response = await callAxios.api({
                    url: web_token_api,
                    method: 'post',
                    token: token,
                })

                dev_console.log(web_token_api);
                dev_console.log(response);

                l_data = response.data;

                if(!response){
                    RTN.sign = true;
                    RTN.data = -2;
                    RTN.msg = "empty reponse";

                    break AccountDataReqeuset;
                }

                if(!isSuccess(response)){
                    RTN.sign = true;
                    RTN.data = -3;
                    RTN.msg = "not nomal response";

                    break AccountDataReqeuset;
                }
            }catch(error){
                dev_console.error(error);

                deleteAllCookies();
            }
        }

        CheckAccountData:{
            if(RTN.sign){
                break CheckAccountData;
            }

            if(l_data === null){
                RTN.sign = true;
                RTN.data = -4;
                RTN.msg = "response data is null";

                break CheckAccountData;
            }

            if(l_data.result.msg !== "valid"){
                RTN.sign = true;
                RTN.data = -5;
                RTN.msg = "invalid token";

                break CheckAccountData;
            }
        }

        CheckOopsThemeAccess:{
            if(RTN.sign || !l_data){
                break CheckOopsThemeAccess;
            }

            if(!canAccessOopsTheme(l_data.result)){
                RTN.sign = true;
                RTN.data = -6;
                RTN.msg = "oops theme access denied";
            }
        }

        // 위 조건으로 처리하는것 자체가 의미가 없을것을 생각해야한다.
        if(RTN.sign){
            deleteAllCookies();
        }

        // DB의 빈 user_type은 그대로 두고, 서버가 명시적으로 OOPS 권한을
        // 허용한 경우에만 브라우저 라우팅용 타입을 E로 보정한다.
        const m_effective_user_type = resolveEffectiveOopsUserType(l_data?.result) || "";

        SetAccountData:{
            if(RTN.sign || !l_data){
                break SetAccountData;
            }

            // 모바일환경일경우 기기 고유 토큰값을 서버로 보낸다.
            await sendDeviceInfo();


            const m_days:number = 0;

            setCookie({name: "user_type", value: m_effective_user_type, days: m_days});
            setCookie({name: "oops_id", value: l_data.result.oops_id, days: m_days});
            setCookie({name: "name", value: l_data.result.name, days: m_days});
            setCookie({name: "user_id", value: `${l_data.result.user_id || ""}`, days: m_days});
            setCookie({name: "office_id", value: `${l_data.result.office_id || ""}`, days: m_days});
            setCookie({name: "office_name", value: l_data.result.office_name, days: m_days});
            setCookie({name: "office_addr", value: l_data.result.office_addr, days: m_days});
            setCookie({name: "office_addr_detail", value: l_data.result.office_addr_detail, days: m_days});
            setCookie({name: "brand_id", value: `${l_data.result.brand_id || ""}`, days: m_days});
            setCookie({name: "brand_name", value: l_data.result.brand_name, days: m_days});

            RTN.sign = true;
            RTN.data = 1;
            RTN.msg = "success";
        }

        SetPage:{
            if(RTN.data < 1){
                setPageRouterCode(pageRouterCode_obj.login);
                break SetPage;
            }

            switch(m_effective_user_type){
                case "E" : // OOPS 관리자
                case "O" : // 일반 직원
                    setPageRouterCode(pageRouterCode_obj.admin);
                    break;
                case "C":
                    setPageRouterCode(pageRouterCode_obj.customer);
                    break;
                default:
                    setPageRouterCode(pageRouterCode_obj.login);
            }
        }
    }

    // 주소에 push_id가 있는 경우, 해당 값을 서버로 보냄
    function pushIdResponse(){
        const m_push_id = backPointUrlManager.getPushId(URLSearchParamsValue);

        if(!m_push_id){
            return;
        }

        const m_url = "api/v3/oops/customer/push/read"

        const config = {
            push_id: m_push_id,
        }

        callAxios.api({
            url: m_url,
            method: "post",
            data: config
        })
        .then((response) => {
            // 성공이던, 실패던, oops에서 취해야할동작은 딱히 없겠지???
            dev_console.error(`${m_url} requset response ===>`, response);

            // 알림 목록 갱신
            alarmListRefetch();

            // 알림 개수 갱신
            alarmNewCountRefetch();
        })
        .catch((error) => {
            dev_console.error(`${m_url} requset error ===>`, error);
        })
        .finally(() => {
            const newParams = new URLSearchParams(URLSearchParamsValue.toString());
            newParams.delete("push_id");

            SetURLSearchParamsValue(newParams, {replace: true});
        })
    }

    const {
        alarmNewCountRefetch,
    } = useAlarmNewCountREST();

    const {
        alarmListRefetch
    } = useAlarmListREST();

    useEffect(() => {
        // 서비스 접근을 막은상태에선 토큰검증처리를 할 필요가 없다고 판단됨.
        if(!l_isMaintenancePageOn){
            const m_token = getAccountData.token();
            authTokenChecker(m_token);
        }
    }, []);

    // 접속한 주소에 push_id가 있다면, 해당 값을 서버로 보냄
    useEffect(() => {
        pushIdResponse();
    }, [URLSearchParamsValue])

    // react-photoswipe-gallery의 모달을 뒤로가기를 통해 닫게하기 위해 전역으로 선언
    useEffect(() => {
        const onPopState = () => {
            const pswpEl = document.querySelector('.pswp');

            if (pswpEl && pswpEl.classList.contains('pswp--open')) {
                // PhotoSwipe 내부의 닫기 버튼 클릭을 트리거
                const closeBtn = pswpEl.querySelector('.pswp__button--close') as HTMLElement;
                closeBtn?.click();
            }
        };

        window.addEventListener('popstate', onPopState);

        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, []);

    // 브라우저 환경 감지
    const l_browserCode = whichBrowserDoYouUse();
    if(l_browserCode === BROWSER_CODE.KakaoTalk || l_browserCode === BROWSER_CODE.LINE){
        return(
            <div className={[CSS.App].join(" ")}>
                <RedirectToAnotherBrowser />
            </div>
        )
    }

    // 페이지에 접근 못하게 막음
    if(l_isMaintenancePageOn){
        return(
            <div className={[CSS.App].join(" ")}>
                <div
                    className={CSS.l_container}
                    ref={containerRef}
                >
                    <main className={CSS.l_main_contents}>
                        <Routes>
                            <Route path="/maintenancePage" element={<MaintenancePage />} />
                            <Route path='*' element={<Navigate replace to='/maintenancePage' />} />
                        </Routes>
                    </main>
                </div>
            </div>
        )
    }else if (pageRouterCode === pageRouterCode_obj.customer) {

        // 고객 로그인 페이지

        return (
            <>
                <div className={CSS.App}>
                    {/* 네비게이션 섹션 */}
                    <div className={[CSS.NaviSection, "NaviSection"].join(" ")}>
                        {/* 네이게이션 메뉴 */}
                        <CustomerNavi />
                    </div>

                    {/* 컨텐츠 섹션 */}
                    <div className={[CSS.ContantsSection, "ContantsSection"].join(" ")}>
                        {/* 헤더 */}
                        <Header
                            headerLeftNode={headerLeftNode}
                            headerRightNode={headerRightNode}
                            mobileHeaderTitle={mobileHeaderTitle}
                            headerBackgroundColorPreset={headerBackgroundColorPreset}
                        />

                        {/* 메인 컨텐츠 영역 */}
                        <div className={[CSS.MainSection, "MainSection"].join(" ")}>

                            <ErrorBoundary>
                                {/* 라우팅 */}
                                <Routes>
                                    {/* <Route path="/" element={<MainHome />} /> */}
                                    <Route path="/" element={<MyHome />} />
                                    <Route path="/myHome" element={<MyHome />} />

                                    <Route path="/myPage" element={<MyPage />} />
                                    <Route path="/changePassword" element={<ChangePassword />} />
                                    <Route path="/myInfo" element={<MyInfo />} />
                                    <Route path="/qnaList" element={<QnaList />} />
                                    <Route path="/qnaList/:postPk" element={<QnaList />} />
                                    <Route path="/qnaList/write" element={<QnaList />} />
                                    <Route path="/qnaList/write/:postPk" element={<QnaList />} />
                                    <Route path="/faqList" element={<FaqList />} />

                                    <Route path="/reservationHome" element={<ReservationHome />} />
                                    <Route path="/selectTheme" element={<SelectTheme />} />

                                    <Route path="/photoList" element={<PhotoListMain />} />
                                    <Route path="/photoDetail" element={<PhotoDetail />} />

                                    <Route path="/productList" element={<ProductList />} />
                                    <Route path="/productDetail" element={<ProductDetail />} />
                                    <Route path="/productConfirm" element={<ProductConfirm />} />
                                    <Route path="/productRequestEdit" element={<ProductRequestEdit />} />

                                    <Route path="/themeBook" element={<ThemeBookHome />} />

                                    <Route path="/paymentHistory" element={<PaymentHistory />} />

                                    {
                                        whatIsMode().is_not_product_mode_safe
                                        &&  <Route path="/test_page" element={<TestPage />} />
                                    }

                                    {
                                        whatIsMode().is_not_product_mode_safe
                                        &&  <Route path="/deleteM" element={<DeleteURL />} />
                                    }

                                    <Route path='*' element={<Navigate replace to='/' />} />
                                </Routes>
                            </ErrorBoundary>

                        </div>
                    </div>
                </div>
                <Toast position={"top-center"}/>
            </>
        );


        // 로그인한 사람이 고객
        // return (
        //     <div className={CSS.App}>
        //         <Toast position={"top-center"}/>

        //         {/* 네비게이션 바 */}
        //         {
        //             !isNaviHidden
        //             &&  <CustomerNavi />
        //         }

        //         <div
        //             className={CSS.l_container}
        //             ref={containerRef}
        //         >
        //             {
        //                 !isHeaderHidden
        //                 &&  <Header
        //                         headerLeftNode={headerLeftNode}
        //                         headerRightNode={headerRightNode}
        //                         mobileHeaderTitle={mobileHeaderTitle}
        //                         headerBackgroundColorPreset={headerBackgroundColorPreset}
        //                     />
        //             }

        //             <main className={CSS.l_main_contents}>
        //                 <ErrorBoundary>
        //                     {/* 라우팅 */}
        //                     <Routes>
        //                         <Route path="/" element={<MainHome />} />
        //                         <Route path="/myHome" element={<MyHome />} />

        //                         <Route path="/myPage" element={<MyPage />} />
        //                         <Route path="/changePassword" element={<ChangePassword />} />

        //                         <Route path="/reservationHome" element={<ReservationHome />} />
        //                         <Route path="/selectTheme" element={<SelectTheme />} />

        //                         <Route path="/photoList" element={<PhotoListMain />} />
        //                         <Route path="/photoDetail" element={<PhotoDetail />} />

        //                         <Route path="/productList" element={<ProductList />} />
        //                         <Route path="/productDetail" element={<ProductDetail />} />
        //                         <Route path="/productConfirm" element={<ProductConfirm />} />
        //                         <Route path="/productRequestEdit" element={<ProductRequestEdit />} />

        //                         <Route path="/themeBook" element={<ThemeBookHome />} />

        //                         <Route path="/paymentHistory" element={<PaymentHistory />} />

        //                         {
        //                             whatIsMode().is_not_product_mode_safe
        //                             &&  <Route path="/test_page" element={<TestPage />} />
        //                         }

        //                         {
        //                             whatIsMode().is_not_product_mode_safe
        //                             &&  <Route path="/deleteM" element={<DeleteURL />} />
        //                         }

        //                         <Route path='*' element={<Navigate replace to='/' />} />
        //                     </Routes>
        //                 </ErrorBoundary>
        //             </main>
        //         </div>
        //     </div>
        // );
    }else if(pageRouterCode === pageRouterCode_obj.admin){
        return (
            <div className={CSS.App}>
                <Toast position={"top-center"}/>

                <div
                    className={CSS.l_container}
                    ref={containerRef}
                >
                    {
                        !isHeaderHidden
                        &&  <Header
                                headerLeftNode={headerLeftNode}
                                headerRightNode={headerRightNode}
                                mobileHeaderTitle={mobileHeaderTitle}
                                headerBackgroundColorPreset={headerBackgroundColorPreset}
                            />
                    }

                    <main className={CSS.l_main_contents}>
                        <ErrorBoundary>
                            <Routes>
                                <Route path='/' element={<ThemeBookHome />} />
                                <Route path="/themeBook" element={<ThemeBookHome />} />
                                <Route path="/themeBookManagement" element={<ThemeBookManagement />} />

                                {
                                    whatIsMode().is_not_product_mode_safe
                                    &&  <Route path="/test_page" element={<TestPage />} />
                                }

                                {
                                    whatIsMode().is_not_product_mode_safe
                                    &&  <Route path="/deleteM" element={<DeleteURL />} />
                                }

                                <Route path='*' element={<Navigate replace to='/' />} />
                            </Routes>
                        </ErrorBoundary>
                    </main>
                </div>

                {
                    !isNaviHidden
                    &&  <AdminNavi />
                }
            </div>
        )
    }else if(pageRouterCode === pageRouterCode_obj.login){
        // 로그인 페이지
        return (
            <div className={CSS.App}>
                <Toast position={"top-left"}/>
                <ErrorBoundary>
                    <Routes>
                        <Route path='/' element={<Login />} />
                        <Route path="/resetUserPassword" element={<ResetUserPassword />} />
                        <Route path="/deleteM" element={<DeleteURL />} />

                        {
                            whatIsMode().is_not_product_mode_safe
                            &&  <Route path="/test_page" element={<TestPage />} />
                        }

                        <Route path='*' element={<Login />} />
                    </Routes>
                </ErrorBoundary>
            </div>
        );
    }else{
        return (
            <div className={CSS.App}>
                <Toast position={"top-left"}/>
                <ErrorBoundary>
                    <Routes>
                        <Route path='*' element={null}/>
                    </Routes>
                </ErrorBoundary>
            </div>
        );
    }
}

export default App;
